import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, mergeMap, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CriterioDto, MetaDto, UnidadMetaDto, EscalaDto, CriterioHijoDto, UnidadBaseDto } from './models';
import { MetaV22 as MetaV22 } from './models/meta-v22';
import { AGRUPADORES } from './models/mock/agrupador-mock';

const API_URL = `${environment.apiUrl}`;
@Injectable({ providedIn: 'root' })

export class UnidadService {
  private API_URL_ESCALA = `${environment.apiUrl}/api/Escalas`;
  private API_URL_METAS = `${environment.apiUrl}/api/v2/metas`;
  private API_URL_CRITERIOS = `${environment.apiUrl}/api/v2/criterios`;
  private API_URL_UNIDADES = `${environment.apiUrl}/api/v2/unidades`;

  private errorMsgeBS = new BehaviorSubject<string>("");
  errorMsge$ = this.errorMsgeBS.asObservable();

  private loadingBS = new BehaviorSubject(false);
  loading$ = this.loadingBS.asObservable();

  private unidadIdBS = new BehaviorSubject<number>(0);
  unidadId$ = this.unidadIdBS.asObservable();

  private params$ = combineLatest([
    this.unidadIdBS.pipe(debounceTime(500)),

  ]).pipe(
    distinctUntilChanged((pre, curr) => {
      return JSON.stringify(pre) === JSON.stringify(curr);
    }),
    map(([
      unidadId,
    ]) => {
      const params: any = {};

      if (unidadId > 0) {
        params.id = unidadId;
      }

      return params;

    })
  );

  metasResponse$ = this.params$.pipe(
    tap(() => this.loadingBS.next(true)),
    debounceTime(500),
    switchMap((params) =>
      this.http.get<{ results: MetaV22[], total: number, elapsed: number }>(
        `${API_URL}/api/v2/metas/unidad/${params.id}`)
    ),
    tap(() => this.loadingBS.next(false)),
    shareReplay(1)
  );

  metasResult$: Observable<MetaV22[]> = this.metasResponse$.pipe(
    map((res) => res.results.filter(m => m.categoriaId >= 12)),
    catchError((err) => this.Error(err, of([])))
  );

  metasCurricular$: Observable<MetaV22[]> = this.metasResponse$.pipe(
    map((res) => res.results.filter(m => m.categoriaId < 12)),
    catchError((err) => this.Error(err, of([])))
  );

  agrupadores$ = of(AGRUPADORES);

  constructor(private http: HttpClient, private snackBar: MatSnackBar) { }

  getEscalas(tipo: string): Observable<EscalaDto[]> {
    return this.http.get<EscalaDto[]>(`${this.API_URL_ESCALA}/tipo/${tipo}`);
  }

  getUnidad(idUnidad: number) {
    return this.http.get<UnidadBaseDto>(`${this.API_URL_UNIDADES}/${idUnidad}`);
  }

  getMetasConCriterios(idUnidad: number): Observable<{ unidad: UnidadBaseDto, unidadMetas: UnidadMetaDto[] }> {
    return this.getUnidad(idUnidad).pipe(
      mergeMap(unidad => this.getCriterios(idUnidad).pipe(
        mergeMap((criterios) => this.getUnidadMetas(idUnidad).pipe(
          map((unidadMetas) => ({
            unidad: unidad,
            unidadMetas: unidadMetas.map(um => {
              return { ...um, meta: { ...um.meta, criterios: criterios.filter(c => c.unidadMetaId === um.id) } }
            }),
          }),
          ))
        ))
      )
    );
  }

  getUnidadMetas(idUnidad: number): Observable<UnidadMetaDto[]> {
    return this.http.get<{ results: UnidadMetaDto[] }>(`${this.API_URL_METAS}/unidad/${idUnidad}`).pipe(
      map((data) => data.results),
    );
  }

  getCriterios(idUnidad: number): Observable<CriterioDto[]> {
    return this.http.get<CriterioDto[]>(`${this.API_URL_CRITERIOS}/unidad/${idUnidad}`);
  }

  updateMeta(meta: MetaDto): Observable<MetaDto> {
    return this.http.put<MetaDto>(`${this.API_URL_METAS}`, meta).pipe(
      map((data) => ({ ...meta, nombre: data.nombre }))
    );
  }

  updateCriterio(criterio: CriterioDto, unidadMetaId: number):
    Observable<{ criterio: CriterioDto, unidadMetaId: number }> {
    return this.http.put<CriterioDto>(`${this.API_URL_CRITERIOS}/idioma`, criterio).pipe(
      map(data => ({ criterio: data, unidadMetaId: unidadMetaId })),
    );
  }

  updateCriterioHijo(criterio: CriterioHijoDto, unidadMetaId: number, criterioId: number):
    Observable<{ criterio: CriterioHijoDto, unidadMetaId: number, criterioId: number }> {
    return this.http.put<CriterioHijoDto>(`${this.API_URL_CRITERIOS}/hijo/idioma`, criterio).pipe(
      map(data => ({ criterio: data, unidadMetaId: unidadMetaId, criterioId: criterioId })),
    );
  }

  setUnidadId(id: number) {
    if (this.unidadIdBS.value !== id) {
      this.unidadIdBS.next(id);
    }
  }

  private Error(err: any, res: Observable<any>) {
    console.log('Error', err?.message);
    this.snackBar.open(
      `Error ${err?.status}: ${err?.statusText}. Reintente la operación.`,
      "Cerrar",
      {
        verticalPosition: "top",
        panelClass: ["error", "error-container"],
      }
    );
    return res;
  }

}
